import { truncateWithEllipsis } from "../../app/helpers/helper";
import CoinIcon from "../../assets/icons/CoinIcon";
import FilterIcon from "../../assets/icons/FilterIcon";
import LeftArrowIcon from "../../assets/icons/LeftArrowIcon";
import RightArrowIcon from "../../assets/icons/RightArrowIcon";
import {
  SupportIssueDidNotReceiveIcon,
  SupportIssueOtherIcon,
  SupportIssueSendIcon,
  SupportIssueUnauthorizedIcon,
  SupportIssueWrongChargeIcon,
} from "../../assets/icons/SupportIcons";
import Loader from "../../components/common/Loader";
import {
  extractTransactionListFromResponse,
  formatTransactionDate,
  resolveTransactionsHasMore,
} from "../../components/common/transactions/transactionUtils";
import TransactionsFilterPanel from "../Transactions/components/TransactionsFilterPanel";
import {
  buildIssueMessage,
  getApiErrorMessage,
  isApiSuccess,
  resolveCreatedTicketId,
  resolveTransactionAmount,
  resolveTransactionDate,
  resolveTransactionId,
  resolveTransactionSubtitle,
  resolveTransactionTitle,
} from "../support/supportUtils";
import gluedin from "gluedin-shorts-js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const paymentModule = new gluedin.GluedInPaymentModule();
const supportModule = new gluedin.GluedInSupportModule();
const TRANSACTION_PAGE_LIMIT = 15;

const getTransactionKey = (transaction: any) =>
  String(
    transaction?.transactionId ||
      transaction?.id ||
      transaction?._id ||
      transaction?.txId ||
      transaction?.referenceId ||
      `${
        transaction?.createdTimestamp ||
        transaction?.createdAt ||
        transaction?.created_at ||
        transaction?.date ||
        ""
      }-${
        transaction?.transactionAmount ??
        transaction?.amount ??
        transaction?.coins ??
        transaction?.value ??
        ""
      }`,
  );

const mergeTransactions = (current: any[], incoming: any[]) => {
  if (!incoming.length) return current;
  const existingKeys = new Set(current.map((item) => getTransactionKey(item)));
  const merged = [...current];

  incoming.forEach((item) => {
    const key = getTransactionKey(item);
    if (existingKeys.has(key)) return;
    existingKeys.add(key);
    merged.push(item);
  });

  return merged;
};

const OTHER_TRANSACTION_ID = "OTHER_TRANSACTION";

const ISSUE_OPTIONS = [
  {
    id: "did_not_receive",
    label: "Didn't receive items",
    icon: <SupportIssueDidNotReceiveIcon />,
  },
  {
    id: "wrong_charge",
    label: "Wrong charge amount",
    icon: <SupportIssueWrongChargeIcon />,
  },
  {
    id: "unauthorized",
    label: "Unauthorized transaction",
    icon: <SupportIssueUnauthorizedIcon />,
  },
  {
    id: "other",
    label: "Other issue",
    icon: <SupportIssueOtherIcon />,
  },
];

const resolveTransactionFlow = (transaction: any) => {
  const flowValue = String(
    transaction?.transactionFlow ||
      transaction?.flow ||
      transaction?.type ||
      transaction?.transactionType ||
      "",
  )
    .trim()
    .toLowerCase();

  if (flowValue.includes("debit") || flowValue.includes("consume")) {
    return "DEBIT";
  }
  if (
    flowValue.includes("credit") ||
    flowValue.includes("reward") ||
    flowValue.includes("earn")
  ) {
    return "CREDIT";
  }

  const amount = resolveTransactionAmount(transaction);
  return amount >= 0 ? "CREDIT" : "DEBIT";
};

const resolveTransactionType = (transaction: any) =>
  String(
    transaction?.transactionType ||
      transaction?.eventType ||
      transaction?.transactionReason ||
      transaction?.event ||
      transaction?.type ||
      "",
  ).trim();

type TransactionFilters = {
  from: Date | null;
  to: Date | null;
  type: string;
};

const cloneFilters = (filters: TransactionFilters): TransactionFilters => ({
  from: filters.from ? new Date(filters.from) : null,
  to: filters.to ? new Date(filters.to) : null,
  type: filters.type,
});

const areFiltersEqual = (
  first: TransactionFilters,
  second: TransactionFilters,
) => {
  const firstFrom = first.from ? first.from.getTime() : null;
  const secondFrom = second.from ? second.from.getTime() : null;
  const firstTo = first.to ? first.to.getTime() : null;
  const secondTo = second.to ? second.to.getTime() : null;
  return (
    firstFrom === secondFrom &&
    firstTo === secondTo &&
    first.type.trim() === second.type.trim()
  );
};

const formatFilterDateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getInitials = (label: string) =>
  label
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const SupportReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const defaultLanguage = localStorage.getItem("defaultLanguage");
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [offset, setOffset] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const filterBtnRef = useRef<HTMLButtonElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const hasMoreRef = useRef(true);
  const isPageRequestInFlightRef = useRef(false);
  const pendingResetRequestRef = useRef(false);
  const hasUserScrolledRef = useRef(false);
  const [filters, setFilters] = useState<TransactionFilters>({
    from: null,
    to: null,
    type: "",
  });
  const [draftFilters, setDraftFilters] = useState<TransactionFilters>({
    from: null,
    to: null,
    type: "",
  });
  const appliedFiltersRef = useRef<TransactionFilters>({
    from: null,
    to: null,
    type: "",
  });
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(
    null,
  );
  const [isOtherTransaction, setIsOtherTransaction] = useState(false);
  const [selectedIssueLabel, setSelectedIssueLabel] = useState("");
  const [description, setDescription] = useState("");
  const [submittedMessage, setSubmittedMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [createdTicketId, setCreatedTicketId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showSelectionScreen = !selectedTransaction && !isOtherTransaction;

  const resolveLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const fetchTransactionsPage = useCallback(
    async (nextOffset: number, append: boolean) => {
      if (isPageRequestInFlightRef.current) {
        if (!append) {
          pendingResetRequestRef.current = true;
        }
        return;
      }
      if (append && !hasMoreRef.current) return;

      isPageRequestInFlightRef.current = true;
      if (append) {
        setIsLoadingMore(true);
      } else {
        setHasMore(true);
        hasMoreRef.current = true;
        setIsLoading(true);
        setErrorMessage("");
      }

      try {
        const requestPayload: Record<string, any> = {
          limit: TRANSACTION_PAGE_LIMIT,
          offset: nextOffset,
        };

        const appliedFilters = appliedFiltersRef.current;
        if (appliedFilters.from) {
          requestPayload.fromDate = formatFilterDateParam(appliedFilters.from);
        }
        if (appliedFilters.to) {
          requestPayload.toDate = formatFilterDateParam(appliedFilters.to);
        }
        const transactionType = appliedFilters.type.trim();
        if (transactionType) {
          requestPayload.transactionType = transactionType;
        }

        const response = await paymentModule.getTransactionList(requestPayload);
        const list = extractTransactionListFromResponse(response);
        const pageHasMore = resolveTransactionsHasMore(
          response,
          list.length,
          TRANSACTION_PAGE_LIMIT,
          nextOffset,
        );

        setTransactions((prev) =>
          append ? mergeTransactions(prev, list) : list,
        );
        setOffset(nextOffset);
        setHasMore(pageHasMore);
        hasMoreRef.current = pageHasMore;

        if (!isApiSuccess(response) && !list.length) {
          setErrorMessage(
            getApiErrorMessage(
              response,
              resolveLabel(
                "label-support-transaction-error",
                "Unable to fetch transactions right now.",
              ),
            ),
          );
        }
      } catch (error: any) {
        if (!append) {
          setTransactions([]);
        }
        setHasMore(false);
        hasMoreRef.current = false;
        setErrorMessage(
          getApiErrorMessage(
            error?.response || error,
            resolveLabel(
              "label-support-transaction-error",
              "Unable to fetch transactions right now.",
            ),
          ),
        );
      } finally {
        isPageRequestInFlightRef.current = false;
        setIsLoading(false);
        setIsLoadingMore(false);
        if (pendingResetRequestRef.current) {
          pendingResetRequestRef.current = false;
          fetchTransactionsPage(1, false);
        }
      }
    },
    [t],
  );

  useEffect(() => {
    appliedFiltersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    pendingResetRequestRef.current = false;
    hasUserScrolledRef.current = false;
    fetchTransactionsPage(1, false);
  }, [fetchTransactionsPage, filters.from, filters.to, filters.type]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    if (!isFilterOpen) return;

    const handleOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (filterRef.current?.contains(target)) return;
      if (filterBtnRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        (target.closest(".react-datepicker") ||
          target.closest(".react-datepicker-popper"))
      ) {
        return;
      }
      setIsFilterOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isFilterOpen]);

  useEffect(() => {
    const markUserScrollIntent = () => {
      hasUserScrolledRef.current = true;
    };
    window.addEventListener("scroll", markUserScrollIntent, { passive: true });
    return () => {
      window.removeEventListener("scroll", markUserScrollIntent);
    };
  }, []);

  useEffect(() => {
    if (!showSelectionScreen || isLoading || isLoadingMore || !hasMore) return;
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasUserScrolledRef.current) return;
        hasUserScrolledRef.current = false;
        fetchTransactionsPage(offset + 1, true);
      },
      {
        root: null,
        rootMargin: "180px 0px",
        threshold: 0,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    fetchTransactionsPage,
    hasMore,
    isLoading,
    isLoadingMore,
    offset,
    showSelectionScreen,
  ]);

  const typeOptions = useMemo(() => {
    const uniqueTypes = new Set<string>();
    transactions.forEach((transaction) => {
      const typeValue = resolveTransactionType(transaction);
      if (typeValue) {
        uniqueTypes.add(typeValue);
      }
    });
    return Array.from(uniqueTypes);
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const hasDateFilter = filters.from || filters.to;
    const hasTypeFilter = filters.type;
    if (!hasDateFilter && !hasTypeFilter) return transactions;

    const fromDate = filters.from;
    const toDate = filters.to;
    const toDateEnd = toDate
      ? new Date(
          toDate.getFullYear(),
          toDate.getMonth(),
          toDate.getDate(),
          23,
          59,
          59,
          999,
        )
      : null;
    const typeFilter = filters.type.trim().toLowerCase();

    return transactions.filter((transaction) => {
      if (typeFilter) {
        const transactionType =
          resolveTransactionType(transaction).toLowerCase();
        if (!transactionType || transactionType !== typeFilter) {
          return false;
        }
      }

      if (hasDateFilter) {
        const transactionDateValue = resolveTransactionDate(transaction);
        const transactionDate = transactionDateValue
          ? new Date(transactionDateValue)
          : null;
        if (!transactionDate || Number.isNaN(transactionDate.getTime())) {
          return false;
        }
        if (fromDate && transactionDate < fromDate) return false;
        if (toDateEnd && transactionDate > toDateEnd) return false;
      }

      return true;
    });
  }, [filters.from, filters.to, filters.type, transactions]);

  const hasActiveDraftFilters = Boolean(
    draftFilters.from || draftFilters.to || draftFilters.type,
  );
  const hasPendingChanges = !areFiltersEqual(filters, draftFilters);

  const handleBack = () => {
    if (submitSuccess) {
      navigate("/support");
      return;
    }
    if (selectedIssueLabel) {
      setSelectedIssueLabel("");
      setDescription("");
      setSubmitError("");
      return;
    }
    if (selectedTransaction || isOtherTransaction) {
      setSelectedTransaction(null);
      setIsOtherTransaction(false);
      setDescription("");
      setSubmitError("");
      return;
    }
    navigate(-1);
  };

  const handleSelectTransaction = (transaction: any) => {
    setIsFilterOpen(false);
    setSelectedTransaction(transaction);
    setIsOtherTransaction(false);
    setSelectedIssueLabel("");
    setDescription("");
    setSubmitError("");
    setSubmitSuccess("");
    setCreatedTicketId("");
    setSubmittedMessage("");
  };

  const handleOtherTransaction = () => {
    setIsFilterOpen(false);
    setSelectedTransaction(null);
    setIsOtherTransaction(true);
    setSelectedIssueLabel("");
    setDescription("");
    setSubmitError("");
    setSubmitSuccess("");
    setCreatedTicketId("");
    setSubmittedMessage("");
  };

  const handleSubmitIssue = async () => {
    setSubmitError("");
    if (isSubmitting) return;

    const detailMessage = description.trim();
    if (!detailMessage) {
      setSubmitError(
        resolveLabel(
          "label-support-description-required",
          "Please describe your issue in detail.",
        ),
      );
      return;
    }

    if (selectedTransaction && !selectedIssueLabel) {
      setSubmitError(
        resolveLabel(
          "label-support-issue-required",
          "Please select an issue type.",
        ),
      );
      return;
    }

    const rawTransactionId = selectedTransaction
      ? resolveTransactionId(selectedTransaction)
      : OTHER_TRANSACTION_ID;
    const transactionId = rawTransactionId || OTHER_TRANSACTION_ID;
    const issueLabel =
      selectedIssueLabel || resolveLabel("label-other", "Other");
    const finalMessage = buildIssueMessage(issueLabel, detailMessage);

    try {
      setIsSubmitting(true);
      const response = await supportModule.sendMessage({
        actionType: "RAISE_ISSUE",
        transactionId,
        title: issueLabel,
        message: finalMessage,
      });

      if (!isApiSuccess(response)) {
        setSubmitError(
          getApiErrorMessage(
            response,
            resolveLabel(
              "label-support-submit-error",
              "Unable to submit this issue right now.",
            ),
          ),
        );
        return;
      }

      setSubmittedMessage(finalMessage);
      setSubmitSuccess(
        resolveLabel(
          "label-support-submit-success",
          "Issue submitted successfully",
        ),
      );
      setCreatedTicketId(resolveCreatedTicketId(response));
      setDescription("");
    } catch (error: any) {
      setSubmitError(
        getApiErrorMessage(
          error?.response || error,
          resolveLabel(
            "label-support-submit-error",
            "Unable to submit this issue right now.",
          ),
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const showIssueOptions = Boolean(selectedTransaction) && !selectedIssueLabel;
  const showDescriptionPrompt =
    isOtherTransaction || Boolean(selectedIssueLabel);
  const canShowInput = showDescriptionPrompt && !submitSuccess;
  const selectedTitle = selectedTransaction
    ? resolveTransactionTitle(selectedTransaction)
    : resolveLabel("label-other-transaction", "Other Transaction");
  const selectedTransactionDate = selectedTransaction
    ? formatTransactionDate(
        resolveTransactionDate(selectedTransaction) || undefined,
      )
    : "";
  const selectedTransactionAmount = selectedTransaction
    ? Math.abs(resolveTransactionAmount(selectedTransaction))
    : 0;
  const selectedIsCredit = selectedTransaction
    ? resolveTransactionFlow(selectedTransaction) !== "DEBIT"
    : true;
  const dateRangeLabel = resolveLabel(
    "label-select-date-range",
    "Select Date Range:",
  );
  const resetLabel = resolveLabel("label-reset", "Reset");
  const fromLabel = resolveLabel("label-from", "From");
  const toLabel = resolveLabel("label-to", "To");
  const typeLabel = resolveLabel("label-type", "Type");
  const selectTypeLabel = resolveLabel(
    "label-select-transaction-type",
    "Select Transaction Type",
  );
  const applyLabel = resolveLabel("label-apply", "Apply");
  const loadingMoreLabel = resolveLabel(
    "label-loading-more-transactions",
    "Loading more transactions...",
  );

  return (
    <div className="full-sec"><div className="right-sec profile-content-list support-page support-report-page">
        <div className="full-box support-full-box">
          <div className="back-header support-header">
            <ul className="list list-inline">
              <li className="back-btn">
                <button
                  className="back-btn-a"
                  type="button"
                  onClick={handleBack}
                >
                  {defaultLanguage === "en" ? (
                    <LeftArrowIcon />
                  ) : (
                    <RightArrowIcon />
                  )}
                  {t("back-btn")}
                </button>
              </li>
              <li className="common-heading">
                {resolveLabel("label-report-issue", "Report an Issue")}
              </li>
            </ul>
          </div>

          {isLoading && <Loader />}

          {!isLoading && showSelectionScreen && (
            <div className="support-report-select">
              <div className="support-report-select__top">
                <div>
                  <h3>
                    {resolveLabel(
                      "label-select-transaction",
                      "Select Transaction",
                    )}
                  </h3>
                  <p>
                    {resolveLabel(
                      "label-select-transaction-sub",
                      "Select the transaction related to your issue",
                    )}
                  </p>
                </div>
                <div className="support-report-select__filter-wrap">
                  <button
                    type="button"
                    className="support-filter-btn"
                    ref={filterBtnRef}
                    onClick={() =>
                      setIsFilterOpen((prev) => {
                        const nextState = !prev;
                        if (nextState) {
                          setDraftFilters(cloneFilters(filters));
                        }
                        return nextState;
                      })
                    }
                    aria-label={resolveLabel("label-filter", "Filter")}
                    aria-expanded={isFilterOpen}
                    aria-controls="transactions-filter-panel"
                  >
                    <FilterIcon />
                  </button>
                  {isFilterOpen && (
                    <TransactionsFilterPanel
                      ref={filterRef}
                      filters={draftFilters}
                      setFilters={setDraftFilters}
                      hasActiveFilters={hasActiveDraftFilters}
                      hasPendingChanges={hasPendingChanges}
                      dateRangeLabel={dateRangeLabel}
                      resetLabel={resetLabel}
                      applyLabel={applyLabel}
                      fromLabel={fromLabel}
                      toLabel={toLabel}
                      typeLabel={typeLabel}
                      selectTypeLabel={selectTypeLabel}
                      typeOptions={typeOptions}
                      formatDate={formatTransactionDate}
                      onApply={() => {
                        hasUserScrolledRef.current = false;
                        setFilters(cloneFilters(draftFilters));
                        setIsFilterOpen(false);
                      }}
                    />
                  )}
                </div>
              </div>

              {errorMessage && (
                <div className="support-error-block support-error-block--small">
                  <p>{errorMessage}</p>
                </div>
              )}

              <div className="support-transaction-list">
                {filteredTransactions.map((transaction, index) => {
                  const title = resolveTransactionTitle(transaction);
                  const subTitle = resolveTransactionSubtitle(transaction);
                  const transactionId = resolveTransactionId(transaction);
                  const transactionDate = formatTransactionDate(
                    resolveTransactionDate(transaction) || undefined,
                  );
                  const amount = Math.abs(
                    resolveTransactionAmount(transaction),
                  );
                  const isCredit =
                    resolveTransactionFlow(transaction) !== "DEBIT";
                  return (
                    <button
                      type="button"
                      key={`${transactionId}-${title}-${transactionDate}-${index}`}
                      className="support-transaction-item"
                      onClick={() => handleSelectTransaction(transaction)}
                    >
                      <div className="support-transaction-item__icon">
                        {getInitials(title) || "TX"}
                      </div>
                      <div className="support-transaction-item__content">
                        <div className="support-transaction-item__title">
                          {title}
                        </div>
                        {subTitle && (
                          <div className="support-transaction-item__subtitle">
                            {subTitle}
                          </div>
                        )}
                      </div>
                      <div className="support-transaction-item__meta">
                        <div className="support-transaction-item__id">
                          {resolveLabel("label-id", "ID:")}{" "}
                          {transactionId
                            ? truncateWithEllipsis(transactionId, 12)
                            : "-"}
                        </div>
                        <div className="support-transaction-item__date">
                          {transactionDate}
                        </div>
                      </div>
                      <div
                        className={`support-transaction-item__amount ${
                          isCredit
                            ? "support-transaction-item__amount--credit"
                            : "support-transaction-item__amount--debit"
                        }`}
                      >
                        {isCredit ? "+" : "-"}
                        {amount}
                        <CoinIcon className="gi-coin-icon gi-coin-small" />
                      </div>
                    </button>
                  );
                })}

                {!errorMessage && filteredTransactions.length === 0 && (
                  <div className="support-empty-card">
                    <p>
                      {resolveLabel(
                        "label-no-transactions",
                        "No transactions yet.",
                      )}
                    </p>
                  </div>
                )}
              </div>
              {isLoadingMore && (
                <div className="transactions-load-more">{loadingMoreLabel}</div>
              )}
              {hasMore && (
                <div
                  ref={loadMoreRef}
                  className="transactions-infinite-trigger"
                  aria-hidden="true"
                />
              )}

              {/* <button
                type="button"
                className="support-other-transaction-btn"
                onClick={handleOtherTransaction}
              >
                {resolveLabel("label-other-transaction", "Other Transaction")}
              </button> */}
            </div>
          )}

          {!isLoading && !showSelectionScreen && (
            <div className="support-report-thread">
              <div className="support-chat-bubble support-chat-bubble--user support-chat-bubble--transaction">
                <div className="support-chat-bubble__transaction-id">
                  {selectedTransaction
                    ? `${resolveLabel(
                        "label-transaction-id",
                        "Transaction ID:",
                      )} ${resolveTransactionId(selectedTransaction) || "-"}`
                    : resolveLabel(
                        "label-other-transaction",
                        "Other Transaction",
                      )}
                </div>
                <div className="flex items-start justify-between">
                  <div className="support-chat-bubble__transaction-title">
                    {selectedTitle}
                  </div>
                  {selectedTransaction && (
                    <div className="support-chat-bubble__transaction-meta">
                      <span>{selectedTransactionDate}</span>
                      <span
                        className={
                          selectedIsCredit
                            ? "support-chat-bubble__transaction-credit"
                            : "support-chat-bubble__transaction-debit"
                        }
                      >
                        {selectedIsCredit ? "+" : "-"}
                        {selectedTransactionAmount}
                        <CoinIcon className="gi-coin-icon gi-coin-small" />
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedTransaction && (
                <div className="support-chat-bubble support-chat-bubble--support">
                  {resolveLabel(
                    "label-what-issue-transaction",
                    "What issue are you experiencing with this transaction?",
                  )}
                </div>
              )}

              {selectedIssueLabel && (
                <div className="support-chat-bubble support-chat-bubble--user">
                  {selectedIssueLabel}
                </div>
              )}

              {showDescriptionPrompt && (
                <div className="support-chat-bubble support-chat-bubble--support">
                  {resolveLabel(
                    "label-describe-issue",
                    "Please describe what happened in detail. The more information you provide, the faster we can help.",
                  )}
                </div>
              )}

              {submittedMessage && (
                <div className="support-chat-bubble support-chat-bubble--user">
                  {submittedMessage}
                </div>
              )}

              {submitSuccess && (
                <div className="support-success-banner">
                  <div className="support-success-banner__icon">
                    <i className="fa fa-check" aria-hidden="true" />
                  </div>
                  <div>
                    <h4>{submitSuccess}</h4>
                    <p>
                      {resolveLabel(
                        "label-support-submit-success-sub",
                        "We'll notify you when there's an update",
                      )}
                    </p>
                  </div>
                </div>
              )}

              {submitError && (
                <div className="support-inline-error">
                  <p>{submitError}</p>
                </div>
              )}

              {showIssueOptions && (
                <div className="support-issue-options">
                  {ISSUE_OPTIONS.map((option) => (
                    <button
                      type="button"
                      key={option.id}
                      className="support-issue-options__item"
                      onClick={() => {
                        setSelectedIssueLabel(option.label);
                        setSubmitError("");
                      }}
                    >
                      <span
                        className="support-issue-options__icon"
                        aria-hidden="true"
                      >
                        {option.icon}
                      </span>
                      {option.label}
                    </button>
                  ))}
                </div>
              )}

              {canShowInput && (
                <div className="support-message-input support-message-input--report">
                  <textarea
                    placeholder={resolveLabel(
                      "label-describe-issue-input",
                      "Describe your issue in detail...",
                    )}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    rows={3}
                    maxLength={500}
                  />
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleSubmitIssue}
                    aria-label={resolveLabel(
                      "label-send-message",
                      "Send message",
                    )}
                  >
                    {isSubmitting ? (
                      <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                    ) : (
                      <SupportIssueSendIcon />
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportReport;
